import { useContext, useEffect, useState } from 'react'

import { ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import Mosaic from '../../assets/images/jaf-institute-mosaic.png'
import { composePath, navigateToInstitute, toTitleCase } from 'src/utils'
import { FormControlLabel } from 'src/components/FormControlLabel'
import { CheckboxComponent } from 'src/components/Checkbox'
import { ButtonLarge } from 'src/components/ButtonLarge'
import { emitNotificationEvent } from 'src/events'
import DataContext from 'src/contexts/DataContext'
import { Loading } from 'src/components/Loading'
import {
  ICandidate,
  ICandidateFile,
  IPkIdAndName,
  useSaveCandidate,
  useSaveCandidateDocumentNew,
} from 'src/services/backend'
import * as S from './styles'
import { useCourseInfoHook } from 'src/hooks/useCourseInfoHook'

export interface IDocuments {
  cpf: string
  checkedIsCpf: boolean
  uploadFile: object
}

export interface ICandidateData {
  name: string
  socialName: string
  motherFullName: string
  fatherFullName: string
  sex: {
    pk_id: number
  }
  registerTypeId: number
  birth_date: string
  whatsapp: string
  whatsappConfirmation: string
  email: string
  emailConfirmation: string
  is_foreign: boolean
  document: string
  issue_entity: { pk_id: number; acronym: string }
  issue_state: IPkIdAndName
  nationality: IPkIdAndName
  issue_date: string
}

export interface IAddress {
  state: string
  city: string
  neighborhood: string
  complemento: string
  number: number
  street: string
  postal_code: string
  undefined: string
}

export interface IResearch {
  state: {
    id: number
    region_id: number
    name: string
    abbreviation: string
    longitude: number
    latitude: number
  }
  city: {
    id: number
    name: string
  }
  school: {
    pk_id: number
    year: number
    name: string
    state_id: number
    city_id: number
  }
  studing_period_type: {
    pk_id: number
    name: string
  }
  institution_type: {
    pk_id: number
    name: string
  }
  school_met: {
    pk_id: number
    name: string
  }
}

export interface IResponsibles {
  responsibleList: [
    {
      name: string
      socialName: string
      sex: {
        pk_id: number
      }
      relationship: {
        pk_id: number
        name: string
      }
      birth_date: string
      cpf: string
      is_foreign: boolean
      document: string
      issue_entity: { pk_id: number; acronym: string }
      issue_state: IPkIdAndName
      issue_date: string
      whatsapp: string
      whatsappConfirmation: string
      email: string
      emailConfirmation: string
      hasAnotherAddress: boolean
      postal_code: string
      street: string
      number: number
      complemento: string
      neighborhood: string
      city: string
      state: string
      undefined: string
      nationality: IPkIdAndName
      civil_state: IPkIdAndName
    }
  ]
}

const TermsPage = () => {
  const toasts = {
    errorToSaveCandidate: (message?: string) =>
      emitNotificationEvent({
        id: 'errorToSaveCandidate',
        type: 'error',
        title: 'Ops',
        message: message
          ? `${message}!`
          : 'Ocorreu um erro ao salvar seus dados. Tente novamente ou contate o suporte clicando acima.',
        isIndeterminate: true,
        textButton: 'Fechar erro',
      }),
    errorToSaveCandidateDocument: () =>
      emitNotificationEvent({
        id: 'errorToSaveCandidateDocument',
        type: 'error',
        title: 'Ops',
        message: 'Ocorreu um erro ao salvar o comprovante de escolaridade.',
        isIndeterminate: true,
        textButton: 'Fechar erro',
      }),
  }

  const navigate = useNavigate()
  const { registration, setRegistration } = useContext(DataContext)

  const currentCourseInfo = useCourseInfoHook({ course: registration.course! })

  const [saveCandidate, saveCandidateResult] = useSaveCandidate()
  const [saveCandidateDocument, saveCandidateDocumentResult] =
    useSaveCandidateDocumentNew()

  const [checkboxesState, setCheckboxesState] = useState<{
    declaration: boolean
    agreement: boolean
  }>({ declaration: false, agreement: false })

  const buildYearsString = () =>
    currentCourseInfo?.year
      ? `${currentCourseInfo?.year}/${currentCourseInfo?.year + 1}`
      : '-/-'

  const getFormDataFromContext = (stepName: string) =>
    registration.getStep(registration, stepName)?.formData

  const contextToRequestBody = (): ICandidate => {
    const search: IResearch = getFormDataFromContext('search')
    const address: IAddress = getFormDataFromContext('address')
    const documents: IDocuments = getFormDataFromContext('documents')
    const responsibles: IResponsibles = getFormDataFromContext('responsibles')
    const candidateData: ICandidateData =
      getFormDataFromContext('candidateData')

    return {
      courseTypeId: registration.courseTypeId!,
      document: {
        isForeign: candidateData.is_foreign,
        registerCode: candidateData.document,
        issueStateId: candidateData.issue_state?.pk_id,
        issueAgencyId: candidateData.issue_entity?.pk_id,
        issueDate: candidateData.issue_date,
      },
      research: {
        schoolId: search.school.pk_id,
        schoolMetTypeId: search.school_met.pk_id,
        schoolPeriodId: search.studing_period_type.pk_id,
        schoolTypeId: search.institution_type.pk_id,
      },
      candidateInfo: {
        name: toTitleCase(candidateData.name),
        socialName: candidateData.socialName,
        cpf: documents.cpf.replaceAll('.', '').replaceAll('-', ''),
        cellphone: candidateData.whatsapp?.replace(/[^0-9.]/g, ''),
        birthDate: candidateData.birth_date,
        sexTypeId: candidateData.sex.pk_id,
        email: candidateData.email,
        motherName: candidateData.motherFullName,
        fatherName: candidateData.fatherFullName,
        nacionalityTypeId: candidateData.nationality.pk_id,
        address: {
          city: address.city,
          complement: address.complemento,
          neighborhood: address.neighborhood,
          number: address.number,
          postalCode: address.postal_code.replaceAll('-', '').toString(),
          publicPlace: address.street,
          ufState: address.state,
        },
      },
      responsibles: responsibles.responsibleList.map((responsible) => ({
        relationTypeId: responsible.relationship.pk_id,
        responsibleInfo: {
          name: toTitleCase(responsible.name),
          socialName: responsible.socialName,
          cpf: responsible.cpf.replaceAll('.', '').replaceAll('-', ''),
          cellphone: responsible.whatsapp?.replace(/[^0-9.]/g, ''),
          birthDate: responsible.birth_date,
          sexTypeId: responsible.sex.pk_id,
          email: responsible.email,
          nacionalityTypeId: responsible.nationality.pk_id,
          maritalStatusType: responsible.civil_state.pk_id,
          address: {
            city: responsible.city || address.city,
            complement: responsible.complemento || address.complemento,
            neighborhood: responsible.neighborhood || address.neighborhood,
            number: responsible.number || address.number,
            postalCode:
              (
                responsible.postal_code &&
                responsible.postal_code.replaceAll('-', '')
              ).toString() ||
              address.postal_code.replaceAll('-', '').toString(),
            publicPlace: responsible.street || address.street,
            ufState: responsible.state || address.state,
          },
        },
        document: {
          isForeign: responsible.is_foreign,
          registerCode: responsible.document,
          issueStateId: responsible.issue_state?.pk_id,
          issueAgencyId: responsible.issue_entity?.pk_id,
          issueDate: responsible.issue_date,
        },
      })),
    }
  }

  const handleDownload = () => {
    if (currentCourseInfo) {
      window.open(currentCourseInfo.terms_url)
    }
  }

  const handleSaveWithFile = () => {
    saveCandidateDocument(
      registration.getStep(registration, 'documents')?.formData?.uploadFile
    )
  }

  const handleSave = (candidateFile?: ICandidateFile) => {
    const body: ICandidate = contextToRequestBody()
    saveCandidate({ ...body, ...(candidateFile && { candidateFile }) })
  }

  useEffect(() => {
    if (saveCandidateDocumentResult?.data) {
      handleSave(saveCandidateDocumentResult?.data?.response)
    }
  }, [saveCandidateDocumentResult?.data])

  useEffect(() => {
    if (!registration.course)
      navigate(`/admissao/${localStorage.getItem('course')}`)
  }, [])

  useEffect(() => {
    if (saveCandidateResult?.data) {
      navigate(composePath('/conclusao', `admissao/${registration.course}`))
      registration.setRegistrationCode(
        setRegistration,
        saveCandidateResult.data.response?.registration_code
      )
    }
  }, [saveCandidateResult?.data])

  useEffect(() => {
    if (saveCandidateResult.error)
      saveCandidateResult.error?.message
        ? toasts.errorToSaveCandidate(saveCandidateResult.error?.message)
        : toasts.errorToSaveCandidate()
  }, [saveCandidateResult.error])

  useEffect(() => {
    if (saveCandidateDocumentResult.status === 201) {
      navigate(composePath('/conclusao', `admissao/${registration.course}`))
    }
  }, [saveCandidateDocumentResult.status])

  useEffect(() => {
    if (saveCandidateResult.error) toasts.errorToSaveCandidateDocument()
  }, [saveCandidateDocumentResult.error])

  return (
    <S.ContainerFlex>
      <S.ContainerImage>
        <img src={Mosaic} alt="Mosaico Instituto J&F" />
      </S.ContainerImage>

      <S.Content>
        <S.Title>Estamos quase lá!</S.Title>

        <S.Subtitle>Leia com atenção os avisos abaixo:</S.Subtitle>

        <S.Paragraph>
          Os resultados de todas as etapas do Processo de Admissão{' '}
          {buildYearsString()} e demais informações serão divulgados em nosso
          site, redes sociais e Área do Candidato,{' '}
          <S.Strong>
            sendo responsabilidade exclusiva do(a) candidato(a) e dos
            responsáveis informarem-se a respeito das etapas.
          </S.Strong>
        </S.Paragraph>

        <FormControlLabel
          control={
            <CheckboxComponent
              checked={checkboxesState.declaration}
              onClick={() =>
                setCheckboxesState((old) => ({
                  ...old,
                  declaration: !old.declaration,
                }))
              }
            />
          }
          label={
            <S.CheckBoxLabel>
              DECLARO QUE AS INFORMAÇÕES ESTÃO CORRETAS
            </S.CheckBoxLabel>
          }
        />

        <FormControlLabel
          control={
            <CheckboxComponent
              checked={checkboxesState.agreement}
              onClick={() =>
                setCheckboxesState((old) => ({
                  ...old,
                  agreement: !old.agreement,
                }))
              }
            />
          }
          label={
            <S.CheckBoxLabel>
              LI E CONCORDO COM OS{' '}
              <S.CheckboxLink onClick={() => handleDownload()}>
                TERMOS E CONDIÇÕES
              </S.CheckboxLink>
            </S.CheckBoxLabel>
          }
        />

        <ButtonLarge
          variant={'contained'}
          endIcon={<ArrowForward />}
          disabled={!checkboxesState.declaration || !checkboxesState.agreement}
          onClick={() =>
            registration.getStep(registration, 'documents')?.formData
              ?.uploadFile
              ? handleSaveWithFile()
              : handleSave()
          }
        >
          CONCLUIR INSCRIÇÃO
        </ButtonLarge>
      </S.Content>

      <Loading
        isLoading={
          saveCandidateResult?.loading || saveCandidateDocumentResult?.loading
        }
      />
    </S.ContainerFlex>
  )
}

export { TermsPage }
